import { Trans, useTranslation } from 'next-i18next'

import { useStateData } from '@/lib/locations'
import {
  Heading3,
  LinkText,
  Paragraph,
  Section3
} from '@/features/Listings/seo/elements'
import ContentTemplate from '@/features/Listings/seo/search-results-content/content/city/content-template'
import getFilterLinks from '@/features/Listings/seo/search-results-content/content/get-filter-links'
import useFilterSlug from '@/features/Listings/seo/search-results-content/content/use-filter-slug'

const GenericCityContent = ({
  filterSlug,
  cityName,
  cityNameWithState,
  cityData,
  isChinese
}) => {
  const { t } = useTranslation()
  const { state, slug: citySlug } = cityData
  const stateData = useStateData(state)
  const { name: stateName } = stateData
  const { hasFilter, filterName } = useFilterSlug(filterSlug)

  const links = getFilterLinks(false, citySlug)

  const getAbout = () => {
    return (
      <Paragraph>
        <Trans i18nKey="seo:About Default USA Paragraph">
          Living in {{ cityName }}, {{ stateName }} is marked by diversity and
          opportunity. The United States is celebrated for its spirit of
          innovation and entrepreneurship, influencing global technology,
          entertainment, and business. Shaped by a history of immigration,
          Americans enjoy a rich cultural tapestry. The rental market in{' '}
          {{ cityName }} caters to a variety of preferences and budgets, with
          options including trendy downtown apartments and charming suburban
          houses. This blend of experiences, coupled with the flexibility that
          renting provides, allows residents to immerse themselves in the
          dynamic and diverse lifestyle that defines the American experience.
        </Trans>
      </Paragraph>
    )
  }

  const getRentingIn = () => {
    const filterNameWithFallback = hasFilter
      ? filterName
      : t('seo:an apartment to rent')

    return (
      <>
        <Paragraph>
          <Trans i18nKey="seo:Renting FILTER in LOCATION in USA Paragraph">
            Finding {{ filterNameWithFallback }} in {{ cityNameWithState }}{' '}
            requires research, communication with{' '}
            <LinkText href="/landlords">landlords & property managers</LinkText>
            , securing references, and signing a lease. All this can add up to a
            lengthy endeavour if you’re not familiar with the rental process in{' '}
            {{ stateName }}. As a result, do your due diligence and be prepared
            with{' '}
            <LinkText href="https://liv.rent/blog/renters/questions-renters-should-ask-landlords-before-they-sign-a-lease/">
              questions to ask your potential landlord
            </LinkText>
            .
          </Trans>
        </Paragraph>
      </>
    )
  }

  const getAverageRent = () => {
    return (
      <Section3>
        <Heading3>
          {hasFilter ? (
            <Trans
              i18nKey="seo:What is the average rent in LOCATION for FILTER?"
              values={{
                filter: filterName,
                location: cityName
              }}
              defaults="What is the average rent in {{ location }} for {{ filter }}?"
            />
          ) : (
            <Trans
              i18nKey="seo:What is the average rent in LOCATION?"
              values={{
                location: cityName
              }}
              defaults="What is the average rent in {{ location }}?"
            />
          )}
        </Heading3>
        <Paragraph>
          {hasFilter ? (
            <Trans i18nKey="seo:Average rent in LOCATION for FILTER paragraph">
              The average rent in {{ cityNameWithState }} for {{ filterName }}{' '}
              will vary on a month-to-month basis. For the latest stats on
              average rent in {{ cityName }}, refer to our{' '}
              <LinkText href="https://liv.rent/blog/rent-reports/">
                monthly rent reports
              </LinkText>
              .
            </Trans>
          ) : (
            <Trans i18nKey="seo:Average rent in LOCATION paragraph">
              The average rent in {{ cityNameWithState }} will vary on a
              month-to-month basis. For the latest stats on average rent in{' '}
              {{ cityName }}, refer to our{' '}
              <LinkText
                href={
                  isChinese
                    ? 'https://liv.rent/blog/zh/%E7%A7%9F%E9%87%91%E7%BB%9F%E8%AE%A1/'
                    : 'https://liv.rent/blog/rent-reports/'
                }
              >
                monthly rent reports
              </LinkText>
              .
            </Trans>
          )}
        </Paragraph>
      </Section3>
    )
  }

  const getApartmentHunting = () => {
    const filterNameWithFallback = hasFilter
      ? filterName
      : t('seo:cheap rentals')

    return (
      <>
        <Paragraph>
          <Paragraph>
            <Trans i18nKey="seo:Apartment Hunting FILTER in LOCATION Paragraph 1">
              Finding {{ filterNameWithFallback }} in {{ cityName }} may be a
              challenge depending on where you’re looking to rent. To increase
              your chances of finding an affordable home, familiarize yourself
              with the different neighbourhoods and the average rent in each
              area.
            </Trans>
          </Paragraph>
        </Paragraph>
        <Paragraph>
          <Trans i18nKey="seo:Apartment Hunting in LOCATION Paragraph 2">
            In addition to choosing your location, it’s important to know what
            rental types are available in {{ cityName }} (e.g.{' '}
            <LinkText href={links.bed1}>1 bedroom apartments</LinkText>,{' '}
            <LinkText href={links.bed2}>2 bedroom apartments</LinkText>,{' '}
            <LinkText href={links.bed3}>3 bedroom apartments</LinkText>,{' '}
            <LinkText href={links.studio}>studio apartments</LinkText>) and the
            average rent for each property type. Refer below to figure out your
            options - ranging from{' '}
            <LinkText href={links.furnished}>furnished rentals</LinkText> and{' '}
            <LinkText href={links.unfurnished}>unfurnished rentals</LinkText> to{' '}
            <LinkText href={links.longTerm}>long term rentals</LinkText> and{' '}
            <LinkText href={links.shortTerm}>short term rentals</LinkText>.
          </Trans>
        </Paragraph>
      </>
    )
  }

  const getRentalTypes = () => {
    return (
      <>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Apartments for rent LOCATION"
              values={{
                location: cityName
              }}
              defaults="Apartments for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo:Apartments for rent LOCATION Paragraph">
              In {{ cityName }}, apartments are a common form of housing you’ll
              find. Average rent prices for apartments depend on where you’re
              looking.{' '}
              <LinkText href={links.apartment}>Apartments for rent</LinkText> in
              urban areas are typically more expensive due to higher demand.
              Apartments generally require much less maintenance than houses,
              making them ideal for renters who don’t need as much space.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Houses for rent LOCATION"
              values={{
                location: cityName
              }}
              defaults="Houses for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo:Houses for rent LOCATION Paragraph">
              There are plenty of{' '}
              <LinkText href={links.house}>houses for rent</LinkText> in{' '}
              {{ cityName }} for those with a higher budget, need extra space,
              or who have multiple roommates. Both partial houses and entire
              houses for rent are common, as well as both new builds and older
              homes. Some areas are also known for an abundance of laneway homes
              and townhomes, making it easy to find a place to rent that suits
              your needs.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Townhouses for rent LOCATION"
              values={{
                location: cityName
              }}
              defaults="Townhouses for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo:Townhouses for rent LOCATION Paragraph">
              Townhouses are another popular form of housing in {{ cityName }}{' '}
              that combine the best qualities of both apartments and whole
              houses. Typically less expensive than houses,{' '}
              <LinkText href={links.townhouse}>townhouses for rent</LinkText>{' '}
              offer an attractive combination of freedom and space, generally
              coming with your own private yard and entrance.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Basements for rent LOCATION"
              values={{
                location: cityName
              }}
              defaults="Basements for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo:Basements for rent LOCATION Paragraph">
              For those looking to find affordable rental housing in{' '}
              {{ cityName }}, basements for rent pose an attractive alternative.
              Since house prices are so expensive, homeowners will sometimes
              rent out their basements as a way to offset their mortgage
              payments. This makes it easy for renters to find relatively
              inexpensive basements for rent in areas throughout the country.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Condos for rent LOCATION"
              values={{
                location: cityName
              }}
              defaults="Condos for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo:Condos for rent LOCATION Paragraph">
              <LinkText href={links.apartment}>Condos for rent</LinkText> are
              also a common sight in {{ cityName }}. These types of rental
              accommodation are perfect for those who like to be in the centre
              of the action, and don’t need as much space. If you are looking to
              rent in the city centre though, be prepared for
              higher-than-average rent prices.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Rooms for rent LOCATION"
              values={{
                location: cityName
              }}
              defaults="Rooms for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo:Rooms for rent LOCATION Paragraph">
              Sharing a house or apartment is practically essential for those
              looking for cheap rent in {{ cityName }}.{' '}
              <LinkText href={links.room}>Rooms for rent</LinkText> are a great
              way to experience wherever you’re living on a budget, and can
              offer you the opportunity to live in neighbourhoods that would
              otherwise be unaffordable.
            </Trans>
          </Paragraph>
        </Section3>
      </>
    )
  }

  const getSpecialTypes = () => {
    return (
      <>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Pet friendly rentals in LOCATION"
              values={{
                location: cityName
              }}
              defaults="Pet friendly rentals in {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo:Pet friendly rentals LOCATION Paragraph">
              Though not every landlord is keen on allowing pets, there are a
              good number of{' '}
              <LinkText href={links.petFriendly}>
                pet-friendly rentals in {{ cityName }}
              </LinkText>{' '}
              for those with furry friends. Both{' '}
              <LinkText href={links.dogFriendly}>dog-friendly</LinkText> and{' '}
              <LinkText href={links.catFriendly}>
                cat-friendly rental units
              </LinkText>{' '}
              are available, as well as properties where both are allowed. Try
              searching for pet-friendly units using search filters on your
              rental platform to find units that match your needs.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Furnished rentals in LOCATION"
              values={{
                location: cityName
              }}
              defaults="Furnished rentals in {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo:Furnished rentals LOCATION Paragraph">
              If you’re new to town or just haven’t had a chance to pick up
              furniture of your own yet, not to worry – there are plenty of{' '}
              <LinkText href={links.furnished}>furnished rentals</LinkText>{' '}
              available in {{ cityName }}. Renters seeking furnished units
              should search for these units using filters, but do be prepared to
              pay more in rent. There are{' '}
              <LinkText
                href={
                  isChinese
                    ? 'https://liv.rent/blog/zh/%e7%a7%9f%e5%ae%a2/%e7%a7%9f%e5%ae%a2%e9%a1%bb%e7%9f%a5%ef%bc%9a%e5%87%ba%e7%a7%9f%e5%8d%95%e4%bd%8d%e5%b8%a6%e5%ae%b6%e5%85%b7%e8%bf%98%e6%98%af%e4%b8%8d%e5%b8%a6%e5%ae%b6%e5%85%b7-%e7%a7%9f%e9%87%91%e5%8c%ba%e5%88%ab/'
                    : 'https://liv.rent/blog/renters/renting-furnished-unfurnished/'
                }
              >
                pros and cons to renting furnished units compared to unfurnished
                units
              </LinkText>
              , so deciding whether it’s worth it to buy furniture when moving
              is a good first step.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Short term rentals in LOCATION"
              values={{
                location: cityName
              }}
              defaults="Short term rentals in {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo:Short term rentals LOCATION Paragraph">
              {{ cityName }} has plenty of work opportunities and excellent
              academic institutions, making it a popular place for those renting
              on a short-term basis.{' '}
              <LinkText href={links.shortTerm}>Short-term rentals</LinkText> are
              exceedingly popular in most cities, particularly during the summer
              months and at the beginning of school semesters. The added
              flexibility is an appealing prospect for many renters, and a key
              factor in{' '}
              <LinkText href="https://liv.rent/blog/landlords/short-term-vs-long-term-rentals-which-are-right-for-you/">
                deciding whether or not short-term rentals are right for you
              </LinkText>
              .
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Cheap rentals in LOCATION"
              values={{
                location: cityName
              }}
              defaults="Cheap rentals in {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo:Cheap rentals LOCATION Generic Paragraph">
              If you’re looking for{' '}
              <LinkText href={links.under1000}>
                cheap rentals in {{ cityNameWithState }}
              </LinkText>
              , try finding a roommate or two and explore shared housing; or
              search for <LinkText href={links.room}>rooms for rent</LinkText>.
              It’s worth noting that in {{ cityName }}, there are neighbourhoods
              where rent is considerably cheaper, so exploring these options is
              a good idea as well.
            </Trans>
          </Paragraph>
        </Section3>
      </>
    )
  }

  const getAmenities = () => {
    return (
      <>
        <Paragraph>
          {hasFilter ? (
            <Trans i18nKey="seo:Rental Amenities FILTER in LOCATION Paragraph 1">
              When you’re searching for{' '}
              <LinkText href={`${links.base}/${filterSlug}`}>
                {{ filterName }} in {{ cityName }}
              </LinkText>
              , make sure to look at what amenities are offered. For instance,
              check to see if the rental includes utilities like air
              conditioning, cable and internet or features such as a dishwasher,
              parking, balcony and fridge.
            </Trans>
          ) : (
            <Trans i18nKey="seo:Rental Amenities LOCATION Paragraph 1">
              When you’re searching for an{' '}
              <LinkText href={links.apartment}>
                apartment to rent in {{ cityName }}
              </LinkText>
              , make sure to look at what amenities are offered. For instance,
              check to see if the rental includes utilities like air
              conditioning, cable and internet or features such as a dishwasher,
              parking, balcony and fridge.
            </Trans>
          )}
        </Paragraph>
        <Paragraph>
          <Trans i18nKey="seo:Rental Amenities LOCATION Paragraph 2">
            Tip: When looking at listings on liv.rent, scroll down to the
            &quot;Amenities&quot; section to see what utilities and features are
            included with the rental.
          </Trans>
        </Paragraph>
      </>
    )
  }

  const getLiving = () => {
    return (
      <Paragraph>
        <Trans i18nKey="seo:Living in LOCATION USA Paragraph">
          Living in {{ cityName }}, {{ stateName }} offers a dynamic experience,
          attracting people from all over the world with its vast array of job
          opportunities, particularly in sectors like technology, healthcare,
          and finance. The country’s melting pot culture provides a rich living
          experience, with a wide range of cuisines, festivals, and cultural
          events. For leisure and recreation, the U.S. boasts everything from
          natural parks and beaches to vibrant cityscapes and historic
          landmarks. This blend of professional opportunities, diverse cultures,
          and a myriad of recreational activities makes the United States a
          sought-after destination for students, professionals, and families
          seeking a dynamic lifestyle.
        </Trans>
      </Paragraph>
    )
  }

  const getPopularHoods = () => {
    return (
      <>
        <Paragraph>
          <Trans i18nKey="seo:Popular Neighbourhoods LOCATION USA Paragraph">
            {{ cityName }}, {{ stateName }} is made up of smaller neighbourhoods
            with their own distinct features. Keep an eye out for our upcoming
            neighbourhood guides focusing on specific cities in USA.
          </Trans>
        </Paragraph>
      </>
    )
  }

  const getLandmarks = () => {
    return (
      <Paragraph>
        <Trans i18nKey="seo:Local Landmarks LOCATION Paragraph">
          Stretching from the Atlantic to the Pacific coasts, the United States
          boasts impressive natural beauty and diverse culture. To get started,
          you can explore this list outlining the{' '}
          <LinkText href="https://www.listchallenges.com/100-landmarks-of-usa">
            top 100 landmarks of USA
          </LinkText>
          .
        </Trans>
      </Paragraph>
    )
  }

  const content = {
    about: getAbout(),
    rentingIn: getRentingIn(),
    averageRent: getAverageRent(),
    apartmentHunting: getApartmentHunting(),
    rentalTypes: getRentalTypes(),
    specialTypes: getSpecialTypes(),
    amenities: getAmenities(),
    living: getLiving(),
    popularHoods: getPopularHoods(),
    landmarks: getLandmarks()
  }

  return (
    <ContentTemplate
      content={content}
      filterSlug={filterSlug}
      cityName={cityName}
      cityNameWithState={cityNameWithState}
      citySlug={citySlug}
      state={state}
      showPopularUniversities={false}
    />
  )
}

export default GenericCityContent
